import {
  ApplicantAnotherExperience,
  ApplicantArticle,
  ApplicantAward,
  ApplicantCommunity,
  ApplicantContest,
  ApplicantEducation,
  ApplicantMentorship,
  ApplicantOpenSource,
  ApplicantPatent,
  ApplicantPublicTalk,
} from "@prisma/client";
import axios from "axios";
import Blob from "cross-blob";
import {
  Document,
  ExternalHyperlink,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";
import isObject from "lodash/isObject";
import { TFunction } from "next-i18next";

import { ExperienceType } from "@/lib/experienceType";
import { getExperienceSectionTitle } from "@/lib/experienceType";
import { prisma } from "@/lib/prisma";

import {
  getRefereeAssignedExperience,
  RefereeGroupName,
} from "@/components/Referees/common";
import {
  getAssessmentDescriptionText,
  getCommonWorksDescriptionText,
  getContactDetails,
  getLevelOfExcitementText,
  getMeetDescriptionText,
  getPersonDescriptionText,
  getPossibleWorksDescriptionText,
} from "@/components/Referees/RefereeDetails";

import { mergeImmigramFormConfig } from "@/core/client/application/applicationForm/config";
import { getApplicationFormConfig } from "@/core/client/application/applicationForm/config/useApplicationFormConfig";

import { docStyles } from "./common";
import { numberingStyle } from "./common/numbering";
import { DocGenerator, DocType } from "../";
import { renderImmigramFormToGoogleDoc } from "../immigramForms";
import { renderProseMirrorSchemaToGoogleDoc } from "../proseMirror";
import { FormWithDataAndConfig } from "../useGenerateDocFromConfigs";
import {
  ApplicantMilestoneFull,
  ApplicantProjectFull,
  ApplicantRefereeFull,
  ApplicationFormPrepared,
  ApplicationFull,
} from "../../prisma";
import { parseProseMirrorSchema } from "../../proseMirror";

interface RefereeDocArgs {
  groupName: RefereeGroupName;
  application: ApplicationFull;
  applicantReferee: ApplicantRefereeFull;
  applicantProjectList?: ApplicantProjectFull[];
  applicantMilestoneList?: ApplicantMilestoneFull[];
  applicantPublicTalkList?: ApplicantPublicTalk[];
  applicantContestList?: ApplicantContest[];
  applicantOpenSourceList?: ApplicantOpenSource[];
  applicantMentorshipList?: ApplicantMentorship[];
  applicantTeachingList?: ApplicantEducation[];
  applicantCommunityList?: ApplicantCommunity[];
  applicantAnotherExperienceList?: ApplicantAnotherExperience[];
  applicantArticleList?: ApplicantArticle[];
  applicantAwardList?: ApplicantAward[];
  applicantPatentList?: ApplicantPatent[];
  applicationProfJourneyFormList?: ApplicationFormPrepared[];
  /** When provided (e.g. from server), used instead of HTTP getApplicationFormConfig to avoid 401 */
  getFormConfig?: (namespace: string) => Promise<any[]>;
}

async function buildJointProjectFormsForReferee(
  arg: RefereeDocArgs
): Promise<FormWithDataAndConfig[]> {
  const selectedIds = new Set(
    (arg.applicantReferee.experienceListAssigned || []).filter(
      (id) => id && id !== "general-info"
    )
  );

  if (
    !selectedIds.size ||
    !arg.applicationProfJourneyFormList ||
    !arg.applicationProfJourneyFormList.length
  ) {
    return [];
  }

  const result: FormWithDataAndConfig[] = [];
  const configCache: Record<string, any[]> = {};

  for (const company of arg.applicationProfJourneyFormList) {
    const experienceType = company.fieldsMap?.experienceType?.value as
      | ExperienceType
      | undefined;

    let docType: DocType | null = null;
    if (experienceType === ExperienceType.Employment) {
      docType = DocType.EmploymentDocV2;
    } else if (experienceType === ExperienceType.Entrepreneurship) {
      docType = DocType.EntrepreneurshipDocV2;
    }

    if (!docType) continue;

    const configData: Record<string, string | null> = {
      careerRoleId: (company.fieldsMap?.careerRoleId?.value as string) || null,
    };

    const projects = company.children || [];

    for (const project of projects) {
      if (!selectedIds.has(project.id)) continue;

      const formsToProcess: ApplicationFormPrepared[] = [
        project,
        ...(project.children || []),
      ];

      for (const form of formsToProcess) {
        if (!form.namespace || !form.fieldsMap) continue;

        let configs = configCache[form.namespace];
        if (!configs) {
          configs = arg.getFormConfig
            ? await arg.getFormConfig(form.namespace)
            : await getApplicationFormConfig(
                arg.application.id,
                form.namespace
              );
          configCache[form.namespace] = configs;
        }

        const mergedConfig = configs.length
          ? mergeImmigramFormConfig(configs, configData)
          : null;

        const filteredConfig =
          mergedConfig &&
          mergedConfig.filter(
            (configElement: any) =>
              isObject(configElement) &&
              Array.isArray((configElement as any).tags) &&
              (configElement as any).tags.includes(docType)
          );

        if (!filteredConfig || !filteredConfig.length) continue;

        result.push({
          values: form.fieldsMap,
          fields: filteredConfig,
        });
      }
    }
  }

  return result;
}

function renderExternalActivitiesForReferee(
  t: TFunction,
  arg: RefereeDocArgs,
  _comments: any[]
): Paragraph[] {
  const selectedIds = new Set(
    arg.applicantReferee.jointExternalProfileActivity || []
  );

  if (!selectedIds.size) {
    return [];
  }

  const activities: Array<{
    id: string;
    type: ExperienceType;
    entity: any;
    created: Date;
  }> = [];

  // Collect all selected activities with their types and creation dates
  arg.applicantArticleList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.Articles,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantAwardList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.Award,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantContestList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.Contests,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantPublicTalkList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.PublicTalks,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantOpenSourceList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.OpenSourceContribution,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantMentorshipList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.TeachingAndMentorship,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantTeachingList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.TeachingAndMentorship,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantCommunityList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.CommunityDevelopment,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantPatentList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.Patent,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  arg.applicantAnotherExperienceList?.forEach((x) => {
    if (selectedIds.has(x.id)) {
      activities.push({
        id: x.id,
        type: ExperienceType.AnotherExperience,
        entity: x,
        created: (x as any).created
          ? new Date((x as any).created)
          : new Date(0),
      });
    }
  });

  // Sort by creation date (oldest first)
  activities.sort((a, b) => {
    const dateA = a.created instanceof Date ? a.created : new Date(a.created);
    const dateB = b.created instanceof Date ? b.created : new Date(b.created);
    return dateA.getTime() - dateB.getTime();
  });

  if (!activities.length) {
    return [];
  }

  // Render each activity using logic from externalProfileDocGeneratorNew
  // For now, create a simplified version - we'll expand it to match the full structure
  const result: Paragraph[] = [];

  for (const activity of activities) {
    const { entity, type } = activity;

    // Add activity title with type
    result.push(
      new Paragraph({
        style: "wellSpaced",
      })
    );
    result.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `${t(getExperienceSectionTitle(type))}: `,
          }),
        ],
        heading: HeadingLevel.HEADING_3,
      })
    );

    // Render based on type - using full logic from externalProfileDocGeneratorNew
    if (type === ExperienceType.PublicTalks) {
      const talk = entity as ApplicantPublicTalk;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Conference title: ", bold: true }),
            new TextRun({
              text: talk.name ? parseProseMirrorSchema(talk.name) : "-",
            }),
          ],
        })
      );
      if (talk.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link to website: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(talk.url),
              }),
            ],
          })
        );
      }
      if (talk.role) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Role: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(talk.role),
              }),
            ],
          })
        );
      }
      if (talk.year) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Year: ", bold: true }),
              new TextRun({
                text: String(talk.year),
              }),
            ],
          })
        );
      }
      if (talk.famousProsNext) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Any famous pros next?: ", bold: true }),
            ],
          })
        );
        const prosParagraphs = renderProseMirrorSchemaToGoogleDoc(
          talk.famousProsNext,
          "-"
        );
        if (prosParagraphs) {
          result.push(...prosParagraphs);
        }
      }
      if (talk.significantStats) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Any significant numbers?: ", bold: true }),
            ],
          })
        );
        const statsParagraphs = renderProseMirrorSchemaToGoogleDoc(
          talk.significantStats,
          "-"
        );
        if (statsParagraphs) {
          result.push(...statsParagraphs);
        }
      }
      if (talk.statsAttendees) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of attendees: ", bold: true }),
              new TextRun({
                text: String(talk.statsAttendees),
              }),
            ],
          })
        );
      }
      if (talk.statsOnlineViewers) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of online viewers: ", bold: true }),
              new TextRun({
                text: String(talk.statsOnlineViewers),
              }),
            ],
          })
        );
      }
      if (talk.statsSpeakers) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of speakers: ", bold: true }),
              new TextRun({
                text: String(talk.statsSpeakers),
              }),
            ],
          })
        );
      }
      if (talk.statsBudget) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Budget: ", bold: true }),
              new TextRun({
                text: String(talk.statsBudget),
              }),
            ],
          })
        );
      }
      if (talk.statsSponsorsPartners) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Number of sponsors or partners: ",
                bold: true,
              }),
              new TextRun({
                text: String(talk.statsSponsorsPartners),
              }),
            ],
          })
        );
      }
      if (talk.statsPublications) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Number of publications about the event: ",
                bold: true,
              }),
              new TextRun({
                text: String(talk.statsPublications),
              }),
            ],
          })
        );
      }
    } else if (type === ExperienceType.Articles) {
      const article = entity as ApplicantArticle;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Article title: ", bold: true }),
            new TextRun({
              text: article.name ? parseProseMirrorSchema(article.name) : "-",
            }),
          ],
        })
      );
      if (article.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link to article: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(article.url),
              }),
            ],
          })
        );
      }
      if (article.description) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Brief description: ", bold: true }),
            ],
          })
        );
        const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
          article.description,
          "-"
        );
        if (descParagraphs) {
          result.push(...descParagraphs);
        }
      }
    } else if (type === ExperienceType.Contests) {
      const contest = entity as ApplicantContest;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Event title: ", bold: true }),
            new TextRun({
              text: contest.name ? parseProseMirrorSchema(contest.name) : "-",
            }),
          ],
        })
      );
      if (contest.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Link to website / pictures: ",
                bold: true,
              }),
              new TextRun({
                text: parseProseMirrorSchema(contest.url),
              }),
            ],
          })
        );
      }
      if (contest.role) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Role: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(contest.role),
              }),
            ],
          })
        );
      }
      if (contest.year) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Year: ", bold: true }),
              new TextRun({
                text: String(contest.year),
              }),
            ],
          })
        );
      }
      if (contest.significantStats) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Any significant numbers?: ", bold: true }),
            ],
          })
        );
        const statsParagraphs = renderProseMirrorSchemaToGoogleDoc(
          contest.significantStats,
          "-"
        );
        if (statsParagraphs) {
          result.push(...statsParagraphs);
        }
      }
      if (contest.statsAttendees) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of attendees: ", bold: true }),
              new TextRun({
                text: String(contest.statsAttendees),
              }),
            ],
          })
        );
      }
      if (contest.statsOnlineViewers) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of online viewers: ", bold: true }),
              new TextRun({
                text: String(contest.statsOnlineViewers),
              }),
            ],
          })
        );
      }
      if (contest.statsEntriesSubmitted) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Number of entries submitted: ",
                bold: true,
              }),
              new TextRun({
                text: String(contest.statsEntriesSubmitted),
              }),
            ],
          })
        );
      }
      if (contest.statsWinners) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of winners: ", bold: true }),
              new TextRun({
                text: String(contest.statsWinners),
              }),
            ],
          })
        );
      }
      if (contest.statsPrizeFund) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Prize fund: ", bold: true }),
              new TextRun({
                text: String(contest.statsPrizeFund),
              }),
            ],
          })
        );
      }
      if (contest.place) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "What place did you get?: ", bold: true }),
            ],
          })
        );
        const placeParagraphs = renderProseMirrorSchemaToGoogleDoc(
          contest.place,
          "-"
        );
        if (placeParagraphs) {
          result.push(...placeParagraphs);
        }
      }
      if (contest.achievements) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "What prize did you receive?: ",
                bold: true,
              }),
            ],
          })
        );
        const achievementsParagraphs = renderProseMirrorSchemaToGoogleDoc(
          contest.achievements,
          "-"
        );
        if (achievementsParagraphs) {
          result.push(...achievementsParagraphs);
        }
      }
    } else if (type === ExperienceType.Award) {
      const award = entity as ApplicantAward;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Award title: ", bold: true }),
            new TextRun({
              text: award.name ? parseProseMirrorSchema(award.name) : "-",
            }),
          ],
        })
      );
      if (award.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link to website: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(award.url),
              }),
            ],
          })
        );
      }
      if (award.description) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Brief description: ", bold: true }),
            ],
          })
        );
        const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
          award.description,
          "-"
        );
        if (descParagraphs) {
          result.push(...descParagraphs);
        }
      }
      if (award.significantStats) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Any significant numbers?: ", bold: true }),
            ],
          })
        );
        const statsParagraphs = renderProseMirrorSchemaToGoogleDoc(
          award.significantStats,
          "-"
        );
        if (statsParagraphs) {
          result.push(...statsParagraphs);
        }
      }
      if (award.statsNominations) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of nominations: ", bold: true }),
              new TextRun({
                text: String(award.statsNominations),
              }),
            ],
          })
        );
      }
      if (award.statsNominees) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of nominees: ", bold: true }),
              new TextRun({
                text: String(award.statsNominees),
              }),
            ],
          })
        );
      }
      if (award.year) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Year: ", bold: true }),
              new TextRun({
                text: String(award.year),
              }),
            ],
          })
        );
      }
    } else if (type === ExperienceType.CommunityDevelopment) {
      const community = entity as ApplicantCommunity;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Title: ", bold: true }),
            new TextRun({
              text: community.name
                ? parseProseMirrorSchema(community.name)
                : "-",
            }),
          ],
        })
      );
      if (community.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link to the community: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(community.url),
              }),
            ],
          })
        );
      }
      if (community.fromYear && community.toYear) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Period: ", bold: true }),
              new TextRun({
                text:
                  Number(community.toYear) === 2099
                    ? `${String(community.fromYear)} - Now`
                    : `${String(community.fromYear)} - ${String(
                        community.toYear
                      )}`,
              }),
            ],
          })
        );
      }
      if (community.communityGoal) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [new TextRun({ text: "Community goal: ", bold: true })],
          })
        );
        const goalParagraphs = renderProseMirrorSchemaToGoogleDoc(
          community.communityGoal,
          "-"
        );
        if (goalParagraphs) {
          result.push(...goalParagraphs);
        }
      }
      if (community.numbersOfAttendee) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of participants: ", bold: true }),
            ],
          })
        );
        const attendeesParagraphs = renderProseMirrorSchemaToGoogleDoc(
          community.numbersOfAttendee,
          "-"
        );
        if (attendeesParagraphs) {
          result.push(...attendeesParagraphs);
        }
      }
      if (community.whoIsCommunityFor) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Who is this community for?: ", bold: true }),
            ],
          })
        );
        const whoParagraphs = renderProseMirrorSchemaToGoogleDoc(
          community.whoIsCommunityFor,
          "-"
        );
        if (whoParagraphs) {
          result.push(...whoParagraphs);
        }
      }
      if (community.significantStats) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Any significant numbers?: ", bold: true }),
            ],
          })
        );
        const statsParagraphs = renderProseMirrorSchemaToGoogleDoc(
          community.significantStats,
          "-"
        );
        if (statsParagraphs) {
          result.push(...statsParagraphs);
        }
      }
    } else if (type === ExperienceType.TeachingAndMentorship) {
      // Check if it's Teaching or Mentorship based on the entity type
      if ("terms" in entity) {
        // Mentorship
        const mentorship = entity as ApplicantMentorship;
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Title: ", bold: true }),
              new TextRun({
                text: mentorship.name
                  ? parseProseMirrorSchema(mentorship.name)
                  : "-",
              }),
            ],
          })
        );
        if (mentorship.url) {
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Link to activity: ", bold: true }),
                new TextRun({
                  text: parseProseMirrorSchema(mentorship.url),
                }),
              ],
            })
          );
        }
        if (mentorship.terms) {
          result.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: "Teaching or mentoring term: ",
                  bold: true,
                }),
                new TextRun({
                  text: parseProseMirrorSchema(mentorship.terms),
                }),
              ],
            })
          );
        }
        if (mentorship.fromYear && mentorship.toYear) {
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Period: ", bold: true }),
                new TextRun({
                  text:
                    Number(mentorship.toYear) === 2099
                      ? `${String(mentorship.fromYear)} - Now`
                      : `${String(mentorship.fromYear)} - ${String(
                          mentorship.toYear
                        )}`,
                }),
              ],
            })
          );
        }
        if (mentorship.what) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "What did you teach: ", bold: true }),
              ],
            })
          );
          const whatParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.what,
            "-"
          );
          if (whatParagraphs) {
            result.push(...whatParagraphs);
          }
        }
        if (mentorship.where) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Where did you teach: ", bold: true }),
              ],
            })
          );
          const whereParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.where,
            "-"
          );
          if (whereParagraphs) {
            result.push(...whereParagraphs);
          }
        }
        if (mentorship.who) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Who did you teach: ", bold: true }),
              ],
            })
          );
          const whoParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.who,
            "-"
          );
          if (whoParagraphs) {
            result.push(...whoParagraphs);
          }
        }
        if (mentorship.tellUsMore) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [new TextRun({ text: "Tell us more: ", bold: true })],
            })
          );
          const tellUsMoreParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.tellUsMore,
            "-"
          );
          if (tellUsMoreParagraphs) {
            result.push(...tellUsMoreParagraphs);
          }
        }
        if (mentorship.studentsNumbers) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Number of students: ", bold: true }),
              ],
            })
          );
          const studentsParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.studentsNumbers,
            "-"
          );
          if (studentsParagraphs) {
            result.push(...studentsParagraphs);
          }
        }
        if (mentorship.description) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Brief description: ", bold: true }),
              ],
            })
          );
          const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.description,
            "-"
          );
          if (descParagraphs) {
            result.push(...descParagraphs);
          }
        }
        if (mentorship.significantStats) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [new TextRun({ text: "Metrics: ", bold: true })],
            })
          );
          const statsParagraphs = renderProseMirrorSchemaToGoogleDoc(
            mentorship.significantStats,
            "-"
          );
          if (statsParagraphs) {
            result.push(...statsParagraphs);
          }
        }
      } else {
        // Teaching
        const teaching = entity as ApplicantEducation;
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Title: ", bold: true }),
              new TextRun({
                text: teaching.name
                  ? parseProseMirrorSchema(teaching.name)
                  : "-",
              }),
            ],
          })
        );
        if (teaching.url) {
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Link to activity: ", bold: true }),
                new TextRun({
                  text: parseProseMirrorSchema(teaching.url),
                }),
              ],
            })
          );
        }
        if (teaching.fromYear && teaching.toYear) {
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Period: ", bold: true }),
                new TextRun({
                  text:
                    Number(teaching.toYear) === 2099
                      ? `${String(teaching.fromYear)} - Now`
                      : `${String(teaching.fromYear)} - ${String(
                          teaching.toYear
                        )}`,
                }),
              ],
            })
          );
        }
        if (teaching.description) {
          result.push(new Paragraph({}));
          result.push(
            new Paragraph({
              children: [
                new TextRun({ text: "Brief description: ", bold: true }),
              ],
            })
          );
          const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
            teaching.description,
            "-"
          );
          if (descParagraphs) {
            result.push(...descParagraphs);
          }
        }
      }
    } else if (type === ExperienceType.OpenSourceContribution) {
      const openSource = entity as ApplicantOpenSource;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Title: ", bold: true }),
            new TextRun({
              text: openSource.name
                ? parseProseMirrorSchema(openSource.name)
                : "-",
            }),
          ],
        })
      );
      if (openSource.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link to the repo: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(openSource.url),
              }),
            ],
          })
        );
      }
      if (openSource.fromYear && openSource.toYear) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Period: ", bold: true }),
              new TextRun({
                text:
                  Number(openSource.toYear) === 2099
                    ? `${String(openSource.fromYear)} - Now`
                    : `${String(openSource.fromYear)} - ${String(
                        openSource.toYear
                      )}`,
              }),
            ],
          })
        );
      }
      if (openSource.description) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Brief description: ", bold: true }),
            ],
          })
        );
        const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
          openSource.description,
          "-"
        );
        if (descParagraphs) {
          result.push(...descParagraphs);
        }
      }
      if (openSource.yourRole) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [new TextRun({ text: "Your role: ", bold: true })],
          })
        );
        const roleParagraphs = renderProseMirrorSchemaToGoogleDoc(
          openSource.yourRole,
          "-"
        );
        if (roleParagraphs) {
          result.push(...roleParagraphs);
        }
      }
      if (openSource.significantStats) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Any significant numbers?: ", bold: true }),
            ],
          })
        );
        const statsParagraphs = renderProseMirrorSchemaToGoogleDoc(
          openSource.significantStats,
          "-"
        );
        if (statsParagraphs) {
          result.push(...statsParagraphs);
        }
      }
      if (openSource.statsStars) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of stars: ", bold: true }),
              new TextRun({
                text: String(openSource.statsStars),
              }),
            ],
          })
        );
      }
      if (openSource.statsForks) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of forks: ", bold: true }),
              new TextRun({
                text: String(openSource.statsForks),
              }),
            ],
          })
        );
      }
      if (openSource.statsDownloads) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of downloads: ", bold: true }),
              new TextRun({
                text: String(openSource.statsDownloads),
              }),
            ],
          })
        );
      }
      if (openSource.statsContributions) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Number of contributions: ", bold: true }),
              new TextRun({
                text: String(openSource.statsContributions),
              }),
            ],
          })
        );
      }
    } else if (type === ExperienceType.Patent) {
      const patent = entity as ApplicantPatent;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Patent title: ", bold: true }),
            new TextRun({
              text: patent.name ? parseProseMirrorSchema(patent.name) : "-",
            }),
          ],
        })
      );
      if (patent.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link to website: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(patent.url),
              }),
            ],
          })
        );
      }
      if (patent.year) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Year: ", bold: true }),
              new TextRun({
                text: String(patent.year),
              }),
            ],
          })
        );
      }
      if (patent.description) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Brief description: ", bold: true }),
            ],
          })
        );
        const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
          patent.description,
          "-"
        );
        if (descParagraphs) {
          result.push(...descParagraphs);
        }
      }
    } else if (type === ExperienceType.AnotherExperience) {
      const another = entity as ApplicantAnotherExperience;
      result.push(
        new Paragraph({
          children: [
            new TextRun({ text: "Title: ", bold: true }),
            new TextRun({
              text: another.name ? parseProseMirrorSchema(another.name) : "-",
            }),
          ],
        })
      );
      if (another.url) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Link: ", bold: true }),
              new TextRun({
                text: parseProseMirrorSchema(another.url),
              }),
            ],
          })
        );
      }
      if (another.fromYear && another.toYear) {
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Period: ", bold: true }),
              new TextRun({
                text:
                  Number(another.toYear) === 2099
                    ? `${String(another.fromYear)} - Now`
                    : `${String(another.fromYear)} - ${String(another.toYear)}`,
              }),
            ],
          })
        );
      }
      if (another.description) {
        result.push(new Paragraph({}));
        result.push(
          new Paragraph({
            children: [
              new TextRun({ text: "Brief description: ", bold: true }),
            ],
          })
        );
        const descParagraphs = renderProseMirrorSchemaToGoogleDoc(
          another.description,
          "-"
        );
        if (descParagraphs) {
          result.push(...descParagraphs);
        }
      }
    }

    result.push(new Paragraph({}));
  }

  return result;
}

export const createRefereeDoc: DocGenerator<RefereeDocArgs> = async (
  t,
  arg
) => {
  const assignedExperienceOptions = getRefereeAssignedExperience(arg, t);

  const jointProjectForms = await buildJointProjectFormsForReferee(arg);

  // Get all comments for the application
  // Try to get comments via API (works both client and server side)
  let comments: any[] = [];
  try {
    // First try API (works on both client and server)
    if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_API_URL) {
      const response = await axios.get<Comment[]>(
        `${process.env.NEXT_PUBLIC_API_URL}/comments/${arg.application.id}`
      );
      comments = response.data || [];
      console.log(`[RefereeDoc] Fetched ${comments.length} comments via API`);
    } else if (typeof prisma !== "undefined" && prisma && prisma.comment) {
      // Fallback to prisma on server side
      comments = await prisma.comment.findMany({
        where: {
          entityId: arg.application.id,
          deleted: null,
        },
        orderBy: {
          created: "asc",
        },
      });
      console.log(
        `[RefereeDoc] Fetched ${comments.length} comments via Prisma`
      );
    }
  } catch (error) {
    console.warn("Failed to fetch comments for referee doc:", error);
    // Continue without comments if fetching fails
  }

  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "Applicant: ",
                bold: true,
              }),
              new TextRun({
                text: arg.application.applicant.fullName
                  ? arg.application.applicant.fullName
                  : `${arg.application.applicant.firstName} ${arg.application.applicant.lastName}`,
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Referee: ",
                bold: true,
              }),
              new TextRun({
                text: `${parseProseMirrorSchema(
                  arg.applicantReferee.firstName
                )} ${parseProseMirrorSchema(arg.applicantReferee.lastName)}`,
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),

          new Paragraph({
            style: "wellSpaced",
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: "Relationship: ",
                bold: true,
              }),
              new TextRun({
                text: arg.applicantReferee.relationshipTag?.name,
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: "Signature likelihood: ",
                bold: true,
              }),
              new TextRun({
                text: arg.applicantReferee.signatureLikelihoodTag?.name,
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: "Assigned project(s): ",
                bold: true,
              }),
              ...(arg.applicantReferee.experienceListAssigned || []).map(
                (x, index) => {
                  const result = assignedExperienceOptions.find(
                    (y) => y.value === x
                  );
                  return new TextRun({
                    text:
                      result !== undefined
                        ? `${result.title}${
                            index <
                            arg.applicantReferee.experienceListAssigned.length -
                              1
                              ? ", "
                              : ""
                          }`
                        : "",
                    bold: false,
                  });
                }
              ),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: "Tags: ",
                bold: true,
              }),
              ...arg.applicantReferee.features.map((x, index) => {
                return new TextRun({
                  text: `${x.name}${
                    index < arg.applicantReferee.features.length - 1 ? ", " : ""
                  } `,
                  bold: false,
                });
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: "LinkedIn: ",
                bold: true,
              }),
              new ExternalHyperlink({
                children: [
                  new TextRun({
                    text: arg.applicantReferee.linkedInUrl
                      ? arg.applicantReferee.linkedInUrl
                      : "",
                    style: "Hyperlink",
                  }),
                ],
                link: arg.applicantReferee.linkedInUrl
                  ? parseProseMirrorSchema(arg.applicantReferee.linkedInUrl)
                  : "",
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: "Worked together at: ",
                bold: true,
              }),
              new TextRun({
                text:
                  arg.applicantReferee.workedTogether &&
                  arg.applicantReferee.companyNameWorkedTogether
                    ? parseProseMirrorSchema(
                        arg.applicantReferee.companyNameWorkedTogether
                      )
                    : "-",
                bold: false,
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: "Working now at company: ",
                bold: true,
              }),
              new TextRun({
                text:
                  arg.applicantReferee.workedTogether &&
                  arg.applicantReferee.companyNameNow
                    ? parseProseMirrorSchema(
                        arg.applicantReferee.companyNameNow
                      )
                    : "-",
                bold: false,
              }),
            ],
          }),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: getPersonDescriptionText(arg.groupName, t).heading + ": ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            arg.applicantReferee.personDescription,
            "-"
          ),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text:
                  getAssessmentDescriptionText(arg.groupName, t).heading + ": ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            arg.applicantReferee.assessmentDescription,
            "-"
          ),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text: getMeetDescriptionText(arg.groupName, t).heading + ": ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            arg.applicantReferee.meetDescription,
            "-"
          ),

          new Paragraph({}),
          new Paragraph({
            children: [
              new TextRun({
                text:
                  getCommonWorksDescriptionText(arg.groupName, t, {
                    isO1Visa:
                      arg.application.visaTypeId === 2 ||
                      arg.application.visaType?.slug === "o1",
                  }).heading + ": ",
                bold: true,
              }),
            ],
          }),

          renderProseMirrorSchemaToGoogleDoc(
            arg.applicantReferee.commonWorksDescription,
            "-"
          ),

          new Paragraph({}),

          arg.groupName === RefereeGroupName.WorkedWith
            ? [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: getPossibleWorksDescriptionText(t).heading + ": ",
                      bold: true,
                    }),
                  ],
                }),
                renderProseMirrorSchemaToGoogleDoc(
                  arg.applicantReferee.possibleWorksDescription,
                  "-"
                ),
              ]
            : [],
          new Paragraph({}),

          new Paragraph({
            children: [
              new TextRun({
                text: getLevelOfExcitementText(t).heading + ": ",
                bold: true,
              }),
            ],
          }),
          renderProseMirrorSchemaToGoogleDoc(
            arg.applicantReferee.levelOfExcitement,
            "-"
          ),

          new Paragraph({}),
          arg.groupName === RefereeGroupName.WorkedWith ||
          arg.groupName === RefereeGroupName.DidntWorkWith
            ? [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: getContactDetails(t).heading + ": ",
                      bold: true,
                    }),
                  ],
                }),
                renderProseMirrorSchemaToGoogleDoc(
                  arg.applicantReferee.contactDetails,
                  "-"
                ),
              ]
            : [],

          // Joint projects section (from Professional Journey forms)
          ...(jointProjectForms.length
            ? [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Joint projects",
                      bold: true,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_2,
                }),
                renderImmigramFormToGoogleDoc(t, jointProjectForms, comments),
              ]
            : []),

          // Activities from external profile section
          ...(arg.applicantReferee.jointExternalProfileActivity &&
          arg.applicantReferee.jointExternalProfileActivity.length > 0
            ? [
                new Paragraph({
                  style: "wellSpaced",
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: "Activities from external profile",
                      bold: true,
                    }),
                  ],
                  heading: HeadingLevel.HEADING_2,
                }),
                ...renderExternalActivitiesForReferee(t, arg, comments),
              ]
            : []),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  const buffer = await Packer.toBuffer(document);
  return new Blob([Uint8Array.from(buffer)]);
};
